// Contagem própria e anônima de visitas e do funil (visita → loja → clique → checkout → pagamento).
// Sem cookie, sem IP e sem dados pessoais: só um código aleatório da aba (sessionStorage) e a origem (UTM/referrer).
// Grava em lote direto no Supabase (sem passar pelas funções do Netlify); sem Supabase configurado, usa /api/events.
import { sourceOf } from '../../netlify/lib/source.mjs';

const URL_ = process.env.REACT_APP_SUPABASE_URL;
const KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const TYPES = {
  page_view: 'pageview', add_to_cart: 'click_product', begin_checkout: 'checkout_start', lead: 'checkout_step2', add_payment_info: 'checkout_pay',
};

let sid = null;
let queue = [];
let timer = null;
let started = false;
const seen = new Set();
let lastView = { path: '', at: 0 };

const off = () => {
  try {
    if (new URLSearchParams(window.location.search).get('pp_notrack') === '1') localStorage.setItem('pp_notrack', '1');
    if (new URLSearchParams(window.location.search).get('pp_notrack') === '0') localStorage.removeItem('pp_notrack');
    return localStorage.getItem('pp_notrack') === '1' || navigator.doNotTrack === '1' || navigator.webdriver;
  } catch { return false; }
};

const session = () => {
  if (sid) return sid;
  try { sid = sessionStorage.getItem('pp_sid'); } catch { /* ignora */ }
  if (!sid) {
    sid = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    try { sessionStorage.setItem('pp_sid', sid); } catch { /* ignora */ }
  }
  return sid;
};

function attr() {
  try { return JSON.parse(localStorage.getItem('pp_attr') || 'null'); } catch { return null; }
}

function flush() {
  timer = null;
  if (!queue.length) return;
  const batch = queue.splice(0, 20);
  try {
    if (URL_ && KEY) {
      fetch(`${URL_}/rest/v1/site_events`, {
        method: 'POST',
        keepalive: true,
        headers: { apikey: KEY, Authorization: `Bearer ${KEY}`, 'Content-Type': 'application/json', Prefer: 'return=minimal' },
        body: JSON.stringify(batch),
      }).catch(() => {});
    } else {
      const body = JSON.stringify({ events: batch });
      if (!(navigator.sendBeacon && navigator.sendBeacon('/api/events', new Blob([body], { type: 'application/json' })))) {
        fetch('/api/events', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
      }
    }
  } catch { /* medir nunca pode quebrar o site */ }
}

// Enquanto a faixa de promoção está no ar, os eventos levam a marca da promoção (para comparar o desempenho com e sem ela).
const withPromo = (meta) => {
  const promo = typeof window !== 'undefined' ? window.__ppPromo : '';
  return promo ? { ...(meta || {}), promo: String(promo).slice(0, 30) } : (meta || null);
};

/** Registra um evento. once = só uma vez por aba (ex.: chegou na loja). */
export function visit(type, meta, once) {
  try {
    if (off() || window.location.pathname.startsWith('/admin')) return;
    if (once) { const k = `${type}:${meta?.id ?? meta?.pct ?? ''}:${window.location.pathname}`; if (seen.has(k)) return; seen.add(k); }
    if (type === 'pageview') { // a mesma página não conta duas vezes em sequência (efeitos duplicados do React)
      // atualizar a página (F5) ou voltar para ela em até 30 min não conta como nova visualização; guardado na aba para valer após recarregar
      let prev = lastView;
      try { prev = JSON.parse(sessionStorage.getItem('pp_lastview') || 'null') || lastView; } catch { /* ignora */ }
      if (prev.path === window.location.pathname && Date.now() - prev.at < 30 * 60 * 1000) return;
      lastView = { path: window.location.pathname, at: Date.now() };
      try { sessionStorage.setItem('pp_lastview', JSON.stringify(lastView)); } catch { /* ignora */ }
    }
    queue.push({
      sid: session(),
      type,
      path: window.location.pathname.slice(0, 200),
      source: sourceOf(attr()).slice(0, 80),
      device: window.innerWidth < 768 ? 'mobile' : 'desktop',
      meta: withPromo(meta),
    });
    if (!timer) timer = setTimeout(flush, 2500);
  } catch { /* ignora */ }
}

/** Ligação com o rastreamento existente: eventos do site viram eventos do funil. */
export const visitFromTrack = (name, params) => {
  if (TYPES[name]) visit(TYPES[name], params?.content_id ? { id: String(params.content_id).slice(0, 40) } : undefined); // com o produto, quando houver
};

/** Rolagem da página, chegada à loja e cliques nos botões principais. Chame uma vez por página. */
export function startVisits() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const check = () => {
    const doc = document.documentElement;
    const total = Math.max(doc.scrollHeight - window.innerHeight, 1);
    const pct = Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / total) * 100));
    [25, 50, 75, 100].forEach((m) => { if (pct >= m && (m < 100 || pct >= 98)) visit('scroll', { pct: m }, true); });
    const shop = document.getElementById('loja');
    if (shop) {
      const r = shop.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.75 && r.bottom > 0) visit('section_view', { id: 'loja' }, true);
    }
  };
  let last = 0;
  const onScroll = () => { if (Date.now() - last > 300) { last = Date.now(); check(); } };
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  setInterval(onScroll, 2000);
  document.addEventListener('click', (e) => {
    const a = e.target?.closest?.('a');
    const href = a?.getAttribute('href') || '';
    if (/^#loja|\/#loja/.test(href)) visit('click_cta');
    else if (/wa\.me|whatsapp/.test(href)) {
      visit('click_whatsapp');
      window.dispatchEvent(new Event('pp:contact')); // avisa os pixels (Google, Meta, TikTok) de um contato
    }
    else if (/\/corporativo|demonstra/.test(href)) visit('click_demo');
  }, true);
  const leave = () => { if (document.visibilityState === 'hidden') flush(); };
  document.addEventListener('visibilitychange', leave);
  window.addEventListener('pagehide', flush);
}
